import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useDispatch, useSelector } from "react-redux";
import { useFetchUser } from "../hooks/user/useFetchUser";
import { useUserLeagues } from "../hooks/league/useFetchUserLeagues";
import { useLeagueMembers } from "../hooks/league/useFetchLeagueMembers";
import { useMultipleLeaguesWithRank } from "../hooks/league/useMultipleLeaguesWithRank";
import useUpsertUser from "../hooks/user/useUpsertUser";
import { useEventsWithLeagueCount } from "../hooks/events/useEventsWithLeagueCount";
import { useUserLeagueSubmissionsCount } from "../hooks/lineup/useUserLeagueSubmissionsCount";
import {
  getEventDisplayType,
  getUpcomingBadgeText,
  isEventThisWeekBeforeCutoff,
} from "../utils/eventTimingHelper";
import {
  resetLineup,
  resetLotn,
  resetAlternateSelections,
  resetAlternateLotn,
  resetProjectedTotal,
} from "../features/lineup/lineupReducer";
import Loading from "../components/common/Loading";
import UserSummaryCard from "../components/home/UserSummaryCard";
import UpcomingEventsCard from "../components/common/UpcomingEventsCard";
import LeaguesCard from "../components/home/LeaguesCard";
import GlobalLeagueCard from "../components/global/GlobalLeagueCard";
import QuickActions from "../components/home/QuickActions";
import UsernameSelectionModal from "../components/user/UsernameSelectionModal";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";

const Home = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth0();
  const currentLeagueId = useSelector((state) => state.league.leagueId);

  const {
    mutate: upsertUser,
    isLoading: isUpsertLoading,
    isError: isUpsertError,
    error: upsertError,
  } = useUpsertUser();
  const {
    data: dbUser,
    isLoading: isUserLoading,
    error: userError,
    refetch: refetchUser,
  } = useFetchUser();

  const {
    data: leaguesData,
    isLoading: isLeaguesLoading,
    isError: isLeaguesError,
  } = useUserLeagues(dbUser?._id);

  const {
    events: eventsWithLeagueCount,
    isLoading: isEventsLoading,
    error: eventsError,
  } = useEventsWithLeagueCount();

  // Extract active league IDs using useMemo
  const activeLeagueIds = useMemo(() => {
    if (!leaguesData?.memberOf) return [];
    return leaguesData.memberOf
      .filter((league) => league.status === "active")
      .map((league) => league._id);
  }, [leaguesData]);

  // Fetch league details with ranks for all active leagues
  const { data: processedLeagues, isLoading: isLeagueDetailsLoading } =
    useMultipleLeaguesWithRank(activeLeagueIds, dbUser?._id);

  const [userData, setUserData] = useState(null);
  const [showUsernameModal, setShowUsernameModal] = useState(false);
  const [loadingTimeout, setLoadingTimeout] = useState(false);

  const globalLeagueId = process.env.REACT_APP_GLOBAL_LEAGUE_ID;
  const { data: leaderboardData, isLoading: isLeaderboardLoading } =
    useLeagueMembers(globalLeagueId, 1, 100, "");
  const { data: globalLeaderboardData, isLoading: isGlobalCardLoading } =
    useLeagueMembers(globalLeagueId, 1, 3, "");

  // Fetch global competition submissions count using hook
  const { data: globalSubmissionsData, isLoading: isLoadingGlobalSubmissions } =
    useUserLeagueSubmissionsCount(dbUser?._id, globalLeagueId);

  // Add a timeout to prevent infinite loading
  useEffect(() => {
    const timer = setTimeout(() => {
      if (isUserLoading || isUpsertLoading) {
        console.error("Loading timeout - forcing render");
        setLoadingTimeout(true);
      }
    }, 10000); // 10 second timeout

    return () => clearTimeout(timer);
  }, [isUserLoading, isUpsertLoading]);

  useEffect(() => {
    const checkAndUpsertUser = async () => {
      if (isAuthenticated && user) {
        let userData = {
          email: user.email,
          auth0Id: user.sub,
          picture: user.picture,
        };

        if (user.name) {
          userData.name = user.name;
        } else if (user.email) {
          const emailPrefix = user.email.split("@")[0];
          userData.name =
            emailPrefix.charAt(0).toUpperCase() + emailPrefix.slice(1);
        }

        // If user fetch completed and user doesn't exist (null or undefined), create them
        if (!isUserLoading && (dbUser === null || dbUser === undefined)) {
          upsertUser(userData, {
            onSuccess: () => {
              // Refetch user data after successful upsert
              refetchUser();
            },
            onError: (error) => {
              console.error("Failed to create user:", error);
            },
          });
        } else if (
          dbUser &&
          (user.email !== dbUser.email || user.name !== dbUser.name)
        ) {
          const updateData = {
            auth0Id: user.sub,
          };

          if (user.email !== dbUser.email) {
            updateData.email = user.email;
          }

          if (user.name !== dbUser.name) {
            updateData.name = userData.name;
          }

          if (updateData.email || updateData.name) {
            upsertUser(updateData, {
              onSuccess: () => {
                refetchUser();
              },
            });
          }
        }
      }
    };

    checkAndUpsertUser();
  }, [user, isAuthenticated, upsertUser, dbUser, isUserLoading, refetchUser]);

  useEffect(() => {
    if (dbUser) {
      dispatch({ type: "SET_USER_ID", payload: dbUser._id });

      let globalRank = null;
      if (leaderboardData && leaderboardData.data) {
        const userIndex = leaderboardData.data.findIndex(
          (member) => member.userId === dbUser._id,
        );
        if (userIndex !== -1) {
          globalRank = userIndex + 1;
        }
      }

      // Count only active leagues
      const activeLeaguesCount =
        leaguesData?.memberOf?.filter((league) => league.status === "active")
          .length || 0;

      setUserData({
        ...dbUser,
        picture: dbUser.picture || user?.picture,
        totalLeagues: activeLeaguesCount,
        globalRank: globalRank,
        globalSubmissions: globalSubmissionsData?.count || 0,
      });
    }
  }, [
    dbUser,
    dispatch,
    user,
    leaguesData,
    leaderboardData,
    globalSubmissionsData,
  ]);

  const handleLeagueClick = (leagueId) => {
    if (currentLeagueId !== leagueId) {
      dispatch(resetLineup());
      dispatch(resetAlternateSelections());
      dispatch(resetLotn());
      dispatch(resetAlternateLotn());
      dispatch(resetProjectedTotal());
    }
    dispatch({ type: "SET_LEAGUE_ID", payload: leagueId });
    navigate(`/league/events`);
  };

  const handleGlobalCompetitionClick = () => {
    const globalLeagueId = process.env.REACT_APP_GLOBAL_LEAGUE_ID;
    if (currentLeagueId !== globalLeagueId) {
      dispatch(resetLineup());
      dispatch(resetAlternateSelections());
      dispatch(resetLotn());
      dispatch(resetAlternateLotn());
      dispatch(resetProjectedTotal());
    }
    dispatch({ type: "SET_LEAGUE_ID", payload: globalLeagueId });
    navigate(`/league/events`);
  };

  // Format events for the UpcomingEventsCard display component
  const formatEventsForDisplay = (events) => {
    if (!events || events.length === 0) return [];

    // Events are already filtered by hasEventPassed in the hook
    // First, prioritize events in the current week, then show upcoming events
    const currentWeekEvents = events.filter((event) =>
      isEventThisWeekBeforeCutoff(event.DateTime),
    );

    const upcomingEvents = events.filter(
      (event) => !isEventThisWeekBeforeCutoff(event.DateTime),
    );

    // Show current week events first, then upcoming
    const eventsToShow = [...currentWeekEvents, ...upcomingEvents].slice(0, 3);

    return eventsToShow.map((event) => {
      const eventDate = new Date(event.DateTime);
      const now = new Date();
      const hasStarted = eventDate <= now;

      const displayType = getEventDisplayType(event.DateTime);
      const showCountdown = displayType === "countdown";
      const showBadge = displayType === "upcoming";
      const badgeText = showBadge ? getUpcomingBadgeText(event.DateTime) : "";

      return {
        id: event.EventId || event._id,
        name: event.Name || event.ShortName,
        monthShort: eventDate
          ? eventDate.toLocaleString("en-US", { month: "short" })
          : "TBA",
        day: eventDate ? eventDate.getDate() : "--",
        formattedTime: eventDate
          ? eventDate.toLocaleTimeString("en-US", {
              hour: "numeric",
              minute: "numeric",
              hour12: true,
            })
          : "TBA",
        showBadge,
        badgeText,
        showCountdown,
        dateTime: event.DateTime,
        leagues: event.userLeagues || [],
        hasStarted,
      };
    });
  };

  useEffect(() => {
    if (dbUser) {
      dispatch({ type: "SET_USER_ID", payload: dbUser._id });
      dispatch({ type: "SET_USER_ROLE", payload: dbUser.role });
    }
  }, [dbUser, dispatch]);

  // Check if user needs to set username
  useEffect(() => {
    if (dbUser && !dbUser.username) {
      setShowUsernameModal(true);
    } else {
      setShowUsernameModal(false);
    }
  }, [dbUser]);

  // Handle username selection completion
  const handleUsernameComplete = () => {
    setShowUsernameModal(false);
    refetchUser(); // Refetch to get updated user with username
  };

  // Show fullscreen loading until ALL initial data is ready
  const isInitialLoading =
    ((isUserLoading || isUpsertLoading) && !loadingTimeout) ||
    isLeaguesLoading ||
    isEventsLoading ||
    isLeaderboardLoading ||
    isGlobalCardLoading ||
    isLeagueDetailsLoading ||
    isLoadingGlobalSubmissions;

  if (isInitialLoading) {
    return <Loading />;
  }

  // Show error if loading timed out
  if (loadingTimeout && !dbUser) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-light-grey">
        <div className="p-6 max-w-md text-center bg-white rounded-lg shadow-card">
          <div className="text-3xl text-deep-red mb-4">Loading Timeout</div>
          <p className="text-medium-grey mb-4">
            We're having trouble loading your account. This might be a temporary
            issue.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-deep-red hover:bg-bright-red text-white rounded-md shadow transition-colors"
          >
            Reload Page
          </button>
        </div>
      </div>
    );
  }

  // const activeLeagues = leaguesData?.memberOf.filter(
  //   (league) => league.status === "active"
  // );

  if (userError || isLeaguesError)
    if (isUpsertError || userError || isLeaguesError || eventsError) {
      return (
        <div className="flex h-screen w-full items-center justify-center bg-light-grey">
          <div className="p-6 max-w-md text-center bg-white rounded-lg shadow-card">
            <div className="text-3xl text-deep-red mb-4">Error</div>
            <p className="text-medium-grey">
              {upsertError?.message ||
                userError?.message ||
                eventsError?.message ||
                "Could not load data."}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 px-4 py-2 bg-deep-red hover:bg-bright-red text-white rounded-md shadow transition-colors"
            >
              Reload
            </button>
          </div>
        </div>
      );
    }

  // Format the events data for display
  const formattedEvents = formatEventsForDisplay(eventsWithLeagueCount);

  // Determine the current week's event for "Set Lineup" quick action
  const currentWeekEvent = eventsWithLeagueCount?.find((event) =>
    isEventThisWeekBeforeCutoff(event.DateTime),
  );
  const nextEventForLineup =
    currentWeekEvent || eventsWithLeagueCount?.[0] || null;

  return (
    <>
      <UsernameSelectionModal
        isOpen={showUsernameModal}
        user={user}
        onComplete={handleUsernameComplete}
      />
      {!showUsernameModal && (
        <div className="space-y-6 pb-6">
          {/* Quick Actions */}
          <QuickActions
            nextEventDate={eventsWithLeagueCount?.[0]?.DateTime || null}
            nextEventId={nextEventForLineup?.EventId || null}
          />

          {/* User Summary (Welcome Back + Stats + Active Submissions) & Global */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <UserSummaryCard user={userData} />
            <GlobalLeagueCard
              buttonText="Enter Global Competition"
              buttonIcon={faArrowRight}
              buttonSize="lg"
              onButtonClick={handleGlobalCompetitionClick}
              leaders={globalLeaderboardData?.data || []}
              totalParticipants={globalLeaderboardData?.total || 0}
              userGlobalEntries={globalSubmissionsData?.count || 0}
              loading={isGlobalCardLoading || isLoadingGlobalSubmissions}
            />
          </div>

          {/* Upcoming Events - Priority Section */}
          <div>
            <UpcomingEventsCard
              events={formattedEvents}
              isLoading={isEventsLoading}
              onEventClick={(eventId, eventName) => {
                dispatch({ type: "SET_EVENT_ID", payload: eventId });
                dispatch({ type: "SET_EVENT_NAME", payload: eventName });
                navigate(`/events/${eventId}`);
              }}
              onViewAllClick={() => navigate("/events")}
              onLeagueClick={(leagueId, eventId) => {
                dispatch({ type: "SET_LEAGUE_ID", payload: leagueId });
                navigate(`/events/${eventId}`);
              }}
            />
          </div>

          {/* Your Leagues */}
          <div>
            <LeaguesCard
              leagues={processedLeagues}
              onLeagueClick={handleLeagueClick}
            />
          </div>

          {/* News Feed - Commented out for future use */}
          {/* <div>
            <TweetFeed />
          </div> */}
        </div>
      )}
    </>
  );
};

export default Home;
